import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faFileAlt,
  faTimes,
  faChevronLeft,
  faChevronRight,
  faExpand,
  faCompress,
  faDatabase,
} from '@fortawesome/free-solid-svg-icons';
import { apiService, API_BASE } from '../services/api';
import { authService } from '../services/auth';
import type { RagDocument, RagDocumentChunk, RagDocumentDetails } from '../types';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Performance timing helper
const logTiming = (label: string, startTime: number) => {
  const elapsed = Date.now() - startTime;
  console.log(`[PDF Viewer] ${label}: ${elapsed}ms`);
  return elapsed;
};

// ============================================================================
// IndexedDB PDF Cache
// ============================================================================
const PDF_CACHE_DB_NAME = 'pdf-cache';
const PDF_CACHE_STORE_NAME = 'pdfs';
const PDF_CACHE_VERSION = 1;
const PDF_CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

interface CachedPDF {
  documentId: string;
  blob: Blob;
  etag?: string;
  cachedAt: number;
  size: number;
}

class PDFCache {
  private dbPromise: Promise<IDBDatabase> | null = null;

  private openDB(): Promise<IDBDatabase> {
    if (this.dbPromise) return this.dbPromise;

    this.dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(PDF_CACHE_DB_NAME, PDF_CACHE_VERSION);

      request.onerror = () => {
        console.error('[PDF Cache] Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        console.log('[PDF Cache] IndexedDB opened successfully');
        resolve(request.result);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(PDF_CACHE_STORE_NAME)) {
          const store = db.createObjectStore(PDF_CACHE_STORE_NAME, { keyPath: 'documentId' });
          store.createIndex('cachedAt', 'cachedAt', { unique: false });
          console.log('[PDF Cache] Created object store');
        }
      };
    });

    return this.dbPromise;
  }

  async get(documentId: string): Promise<CachedPDF | null> {
    try {
      const db = await this.openDB();
      return new Promise((resolve) => {
        const tx = db.transaction(PDF_CACHE_STORE_NAME, 'readonly');
        const store = tx.objectStore(PDF_CACHE_STORE_NAME);
        const request = store.get(documentId);

        request.onsuccess = () => {
          const cached = request.result as CachedPDF | undefined;
          if (cached) {
            // Check if cache is expired
            if (Date.now() - cached.cachedAt > PDF_CACHE_MAX_AGE_MS) {
              console.log(`[PDF Cache] Cache expired for ${documentId}`);
              this.delete(documentId); // Clean up async
              resolve(null);
            } else {
              console.log(`[PDF Cache] Cache hit for ${documentId} (${(cached.size / 1024 / 1024).toFixed(2)} MB)`);
              resolve(cached);
            }
          } else {
            resolve(null);
          }
        };

        request.onerror = () => {
          console.error('[PDF Cache] Get error:', request.error);
          resolve(null);
        };
      });
    } catch (err) {
      console.error('[PDF Cache] Get failed:', err);
      return null;
    }
  }

  async set(documentId: string, blob: Blob, etag?: string): Promise<void> {
    try {
      const db = await this.openDB();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(PDF_CACHE_STORE_NAME, 'readwrite');
        const store = tx.objectStore(PDF_CACHE_STORE_NAME);

        const cached: CachedPDF = {
          documentId,
          blob,
          etag,
          cachedAt: Date.now(),
          size: blob.size,
        };

        const request = store.put(cached);

        request.onsuccess = () => {
          console.log(`[PDF Cache] Cached ${documentId} (${(blob.size / 1024 / 1024).toFixed(2)} MB)`);
          resolve();
        };

        request.onerror = () => {
          console.error('[PDF Cache] Set error:', request.error);
          reject(request.error);
        };
      });
    } catch (err) {
      console.error('[PDF Cache] Set failed:', err);
    }
  }

  async delete(documentId: string): Promise<void> {
    try {
      const db = await this.openDB();
      return new Promise((resolve) => {
        const tx = db.transaction(PDF_CACHE_STORE_NAME, 'readwrite');
        const store = tx.objectStore(PDF_CACHE_STORE_NAME);
        store.delete(documentId);
        tx.oncomplete = () => resolve();
      });
    } catch (err) {
      console.error('[PDF Cache] Delete failed:', err);
    }
  }

  async getCacheStats(): Promise<{ count: number; totalSize: number }> {
    try {
      const db = await this.openDB();
      return new Promise((resolve) => {
        const tx = db.transaction(PDF_CACHE_STORE_NAME, 'readonly');
        const store = tx.objectStore(PDF_CACHE_STORE_NAME);
        const request = store.getAll();

        request.onsuccess = () => {
          const items = request.result as CachedPDF[];
          const totalSize = items.reduce((sum, item) => sum + item.size, 0);
          resolve({ count: items.length, totalSize });
        };

        request.onerror = () => resolve({ count: 0, totalSize: 0 });
      });
    } catch {
      return { count: 0, totalSize: 0 };
    }
  }
}

// Singleton cache instance
const pdfCache = new PDFCache();

// Colors
const DARK_COLOR = '#444444';
const ACCENT_COLOR = '#fec35c';
const ERROR_COLOR = '#ef4444';
const INFO_COLOR = '#3b82f6';
const PURPLE_COLOR = '#8b5cf6';

export interface DocumentViewerModalProps {
  document: RagDocument;
  onClose: () => void;
  /** Optional: Pre-select a specific chunk by ID */
  initialChunkId?: string;
  /** Optional: Start on a specific page */
  initialPage?: number;
}

export const DocumentViewerModal: React.FC<DocumentViewerModalProps> = ({
  document,
  onClose,
  initialChunkId,
  initialPage,
}) => {
  const [documentDetails, setDocumentDetails] = useState<RagDocumentDetails | null>(null);
  const [pdfData, setPdfData] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(initialPage || 1);
  const [scale, setScale] = useState<number>(1.0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedChunk, setSelectedChunk] = useState<RagDocumentChunk | null>(null);
  const [hoveredChunk, setHoveredChunk] = useState<RagDocumentChunk | null>(null);

  // Download progress tracking
  const [downloadProgress, setDownloadProgress] = useState<number>(0);
  const [downloadStatus, setDownloadStatus] = useState<string>('');
  const [fromCache, setFromCache] = useState<boolean>(false);

  // Performance metrics ref
  const metricsRef = useRef<{ fetchStart: number; pdfStart: number; }>({ fetchStart: 0, pdfStart: 0 });

  // Fetch document details and PDF file with caching
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      setDownloadProgress(0);
      setFromCache(false);
      metricsRef.current.fetchStart = Date.now();

      try {
        // Fetch document details with chunks (parallel with PDF)
        setDownloadStatus('Loading document metadata...');
        const detailsPromise = apiService.getRagDocumentDetails(document.id, true);

        // Try cache first, then fetch if needed
        setDownloadStatus('Checking cache...');
        let pdfBlob: Blob | null = null;
        const cached = await pdfCache.get(document.id);

        if (cached) {
          // Use cached PDF
          pdfBlob = cached.blob;
          setFromCache(true);
          setDownloadProgress(100);
          logTiming('PDF loaded from cache', metricsRef.current.fetchStart);
        } else {
          // Fetch from server with progress tracking
          setDownloadStatus('Downloading PDF...');
          pdfBlob = await fetchPdfWithProgress(document.id);

          // Cache the downloaded PDF (async, don't await)
          if (pdfBlob) {
            pdfCache.set(document.id, pdfBlob).catch(() => {
              // Silently fail cache storage - not critical
            });
          }
        }

        // Wait for details (may already be done)
        const details = await detailsPromise;
        logTiming('Total fetch time', metricsRef.current.fetchStart);

        if (details) {
          setDocumentDetails(details);

          // If initialChunkId provided, find and select that chunk
          if (initialChunkId && details.chunks) {
            const chunk = details.chunks.find(c =>
              c.id === initialChunkId || c.azure_search_id === initialChunkId
            );
            if (chunk) {
              setSelectedChunk(chunk);
              if (chunk.bounding_regions?.[0]?.pageNumber) {
                setCurrentPage(chunk.bounding_regions[0].pageNumber);
              }
            }
          }
        }

        if (pdfBlob) {
          setDownloadStatus('Preparing PDF viewer...');
          metricsRef.current.pdfStart = Date.now();
          const url = URL.createObjectURL(pdfBlob);
          setPdfData(url);
        } else {
          setError('Failed to load PDF file');
        }
      } catch (err) {
        setError('Failed to load document');
        console.error(err);
      } finally {
        setIsLoading(false);
        setDownloadStatus('');
      }
    };

    // Fetch PDF with XMLHttpRequest for progress tracking
    const fetchPdfWithProgress = (documentId: string): Promise<Blob | null> => {
      return new Promise((resolve) => {
        const xhr = new XMLHttpRequest();
        const url = `${API_BASE}/ingest/documents/${documentId}/file`;

        xhr.open('GET', url, true);
        xhr.responseType = 'blob';

        // Add auth header if available
        const token = authService.getAccessToken();
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }

        xhr.onprogress = (event) => {
          if (event.lengthComputable) {
            const percent = Math.round((event.loaded / event.total) * 100);
            setDownloadProgress(percent);
            setDownloadStatus(`Downloading PDF... ${percent}%`);
          } else {
            // If length not computable, show bytes downloaded
            const mb = (event.loaded / (1024 * 1024)).toFixed(1);
            setDownloadStatus(`Downloading PDF... ${mb} MB`);
          }
        };

        xhr.onload = () => {
          if (xhr.status === 200) {
            logTiming('PDF download complete', metricsRef.current.fetchStart);
            resolve(xhr.response as Blob);
          } else {
            console.error('PDF download failed:', xhr.status);
            resolve(null);
          }
        };

        xhr.onerror = () => {
          console.error('PDF download error');
          resolve(null);
        };

        xhr.send();
      });
    };

    fetchData();

    // Cleanup blob URL on unmount
    return () => {
      if (pdfData) {
        URL.revokeObjectURL(pdfData);
      }
    };
  }, [document.id, initialChunkId, pdfData]);

  // Get chunks for current page
  const chunksForCurrentPage = useMemo(() => {
    if (!documentDetails?.chunks) return [];
    return documentDetails.chunks.filter(chunk =>
      chunk.bounding_regions?.some(region => region.pageNumber === currentPage)
    );
  }, [documentDetails?.chunks, currentPage]);

  // Handle PDF load success
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    logTiming('PDF parsed and ready', metricsRef.current.pdfStart);
    console.log(`[PDF Viewer] Document has ${numPages} pages`);
    setNumPages(numPages);
  };

  // Handle page render start - just for logging
  const onPageLoadStart = useCallback(() => {
    console.log(`[PDF Viewer] Starting to render page ${currentPage}`);
  }, [currentPage]);

  // Handle page render success
  const onPageRenderSuccess = useCallback(() => {
    logTiming(`Page ${currentPage} rendered`, metricsRef.current.pdfStart);
  }, [currentPage]);

  // Convert polygon points to SVG path
  // Polygon format: [x1, y1, x2, y2, x3, y3, x4, y4] (normalized 0-1 or in inches)
  const polygonToPath = (polygon: number[]): string => {
    if (!polygon || polygon.length < 8) return '';

    // Azure Document Intelligence returns coordinates in inches (assuming 72 DPI for PDF)
    // We need to scale them to our rendered page size
    const points: string[] = [];
    for (let i = 0; i < polygon.length; i += 2) {
      // Convert from inches to pixels (72 DPI base, scaled by our scale factor)
      const x = polygon[i] * 72 * scale;
      const y = polygon[i + 1] * 72 * scale;
      points.push(`${x},${y}`);
    }
    return `M ${points.join(' L ')} Z`;
  };

  // Navigation handlers
  const goToPrevPage = useCallback(() => setCurrentPage(p => Math.max(1, p - 1)), []);
  const goToNextPage = useCallback(() => setCurrentPage(p => Math.min(numPages, p + 1)), [numPages]);
  const zoomIn = useCallback(() => setScale(s => Math.min(2.5, s + 0.25)), []);
  const zoomOut = useCallback(() => setScale(s => Math.max(0.5, s - 0.25)), []);

  // Get chunk color based on content type
  const getChunkColor = (chunk: RagDocumentChunk, isHovered: boolean, isSelected: boolean) => {
    const baseOpacity = isSelected ? 0.4 : isHovered ? 0.3 : 0.15;
    if (chunk.content_type === 'table') {
      return `rgba(139, 92, 246, ${baseOpacity})`; // Purple for tables
    }
    return `rgba(59, 130, 246, ${baseOpacity})`; // Blue for paragraphs
  };

  const getChunkBorderColor = (chunk: RagDocumentChunk) => {
    if (chunk.content_type === 'table') {
      return PURPLE_COLOR;
    }
    return INFO_COLOR;
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        goToPrevPage();
      } else if (e.key === 'ArrowRight') {
        goToNextPage();
      } else if (e.key === '+' || e.key === '=') {
        zoomIn();
      } else if (e.key === '-') {
        zoomOut();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToPrevPage, goToNextPage, zoomIn, zoomOut, onClose]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Modal Container */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: '1400px',
          height: '100%',
          maxHeight: 'calc(100vh - 4rem)',
          background: '#f8fafc',
          borderRadius: '4px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            background: DARK_COLOR,
            padding: '0.875rem 1.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            color: 'white',
            flexShrink: 0,
            borderRadius: '4px 4px 0 0',
          }}
        >
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <FontAwesomeIcon icon={faFileAlt} />
          <span style={{ fontWeight: 600 }}>{document.filename}</span>
          {numPages > 0 && (
            <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
              Page {currentPage} of {numPages}
            </span>
          )}
          {fromCache && (
            <span
              title="Loaded from browser cache"
              style={{
                fontSize: '0.7rem',
                color: '#22c55e',
                background: 'rgba(34, 197, 94, 0.15)',
                padding: '0.2rem 0.5rem',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
              }}
            >
              <FontAwesomeIcon icon={faDatabase} />
              Cached
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {/* Zoom controls */}
          <button
            onClick={zoomOut}
            title="Zoom out (-)"
            style={{
              padding: '0.5rem',
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: '4px',
              color: 'white',
              cursor: 'pointer',
            }}
          >
            <FontAwesomeIcon icon={faCompress} />
          </button>
          <span style={{ fontSize: '0.85rem', minWidth: '3rem', textAlign: 'center' }}>
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={zoomIn}
            title="Zoom in (+)"
            style={{
              padding: '0.5rem',
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: '4px',
              color: 'white',
              cursor: 'pointer',
            }}
          >
            <FontAwesomeIcon icon={faExpand} />
          </button>
          {/* Page navigation */}
          <div style={{ marginLeft: '1rem', display: 'flex', gap: '0.25rem' }}>
            <button
              onClick={goToPrevPage}
              disabled={currentPage <= 1}
              title="Previous page (Left arrow)"
              style={{
                padding: '0.5rem 0.75rem',
                background: currentPage <= 1 ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: '4px',
                color: currentPage <= 1 ? '#64748b' : 'white',
                cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
              }}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <button
              onClick={goToNextPage}
              disabled={currentPage >= numPages}
              title="Next page (Right arrow)"
              style={{
                padding: '0.5rem 0.75rem',
                background: currentPage >= numPages ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: '4px',
                color: currentPage >= numPages ? '#64748b' : 'white',
                cursor: currentPage >= numPages ? 'not-allowed' : 'pointer',
              }}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
          {/* Close button */}
          <button
            onClick={onClose}
            title="Close (Esc)"
            style={{
              marginLeft: '1rem',
              padding: '0.5rem 0.75rem',
              background: ACCENT_COLOR,
              border: 'none',
              borderRadius: '4px',
              color: 'white',
              cursor: 'pointer',
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* PDF Viewer */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            padding: '1rem',
            background: '#1e1e1e',
          }}
        >
          {isLoading ? (
            <div style={{ color: 'white', padding: '2rem', textAlign: 'center' }}>
              <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.5rem', fontSize: '1.5rem' }} />
              <div style={{ marginTop: '1rem', fontSize: '0.9rem' }}>{downloadStatus || 'Loading document...'}</div>
              {downloadProgress > 0 && (
                <div style={{ marginTop: '1rem', width: '200px', margin: '1rem auto' }}>
                  <div style={{
                    height: '6px',
                    background: 'rgba(255,255,255,0.2)',
                    borderRadius: '4px',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      width: `${downloadProgress}%`,
                      height: '100%',
                      background: ACCENT_COLOR,
                      transition: 'width 0.2s ease',
                    }} />
                  </div>
                </div>
              )}
            </div>
          ) : error ? (
            <div style={{ color: ERROR_COLOR, padding: '2rem' }}>{error}</div>
          ) : pdfData ? (
            <div style={{ position: 'relative' }}>
              <Document
                file={pdfData}
                onLoadSuccess={onDocumentLoadSuccess}
                loading={
                  <div style={{ color: 'white', padding: '2rem' }}>
                    <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.5rem' }} />
                    Loading PDF...
                  </div>
                }
              >
                <Page
                  pageNumber={currentPage}
                  scale={scale}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                  onLoadStart={onPageLoadStart}
                  onRenderSuccess={onPageRenderSuccess}
                  loading={
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: '400px',
                      minWidth: '300px',
                      color: 'white',
                    }}>
                      <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.5rem' }} />
                      Rendering page {currentPage}...
                    </div>
                  }
                />
              </Document>

              {/* Chunk boundary overlays */}
              <svg
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                }}
              >
                {chunksForCurrentPage.map((chunk) => {
                  const regions = chunk.bounding_regions?.filter(r => r.pageNumber === currentPage) || [];
                  return regions.map((region, idx) => (
                    <path
                      key={`${chunk.id}-${idx}`}
                      d={polygonToPath(region.polygon)}
                      fill={getChunkColor(chunk, hoveredChunk?.id === chunk.id, selectedChunk?.id === chunk.id)}
                      stroke={getChunkBorderColor(chunk)}
                      strokeWidth={selectedChunk?.id === chunk.id ? 2 : 1}
                      style={{ pointerEvents: 'all', cursor: 'pointer' }}
                      onMouseEnter={() => setHoveredChunk(chunk)}
                      onMouseLeave={() => setHoveredChunk(null)}
                      onClick={() => setSelectedChunk(chunk)}
                    />
                  ));
                })}
              </svg>
            </div>
          ) : null}
        </div>

        {/* Chunk Info Sidebar */}
        <div
          style={{
            width: '350px',
            background: '#f8fafc',
            borderLeft: '1px solid #e2e8f0',
            overflow: 'auto',
            flexShrink: 0,
          }}
        >
          <div style={{ padding: '1rem' }}>
            <h3 style={{ margin: '0 0 1rem', color: DARK_COLOR, fontSize: '0.9rem', fontWeight: 600 }}>
              Chunks on Page {currentPage}
            </h3>

            {chunksForCurrentPage.length === 0 ? (
              <div style={{ color: '#64748b', fontSize: '0.85rem', textAlign: 'center', padding: '2rem' }}>
                No chunks found on this page
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {chunksForCurrentPage.map((chunk) => (
                  <div
                    key={chunk.id}
                    onClick={() => setSelectedChunk(chunk)}
                    onMouseEnter={() => setHoveredChunk(chunk)}
                    onMouseLeave={() => setHoveredChunk(null)}
                    style={{
                      padding: '0.75rem',
                      background: selectedChunk?.id === chunk.id ? '#e0f2fe' : hoveredChunk?.id === chunk.id ? '#f1f5f9' : 'white',
                      borderRadius: '4px',
                      border: `1px solid ${selectedChunk?.id === chunk.id ? INFO_COLOR : '#e2e8f0'}`,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span
                        style={{
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          color: 'white',
                          background: chunk.content_type === 'table' ? PURPLE_COLOR : INFO_COLOR,
                          padding: '0.15rem 0.4rem',
                          borderRadius: '4px',
                          textTransform: 'uppercase',
                        }}
                      >
                        {chunk.content_type}
                      </span>
                      <span style={{ fontSize: '0.7rem', color: '#64748b' }}>
                        Chunk #{chunk.chunk_index}
                      </span>
                    </div>

                    {chunk.heading_context && (
                      <div style={{
                        fontSize: '0.75rem',
                        color: '#6366f1',
                        fontWeight: 500,
                        marginBottom: '0.5rem',
                      }}>
                        {chunk.heading_context}
                      </div>
                    )}

                    {chunk.summary && (
                      <div style={{ marginBottom: '0.5rem' }}>
                        <div style={{ fontSize: '0.65rem', color: '#94a3b8', marginBottom: '0.25rem', fontWeight: 600 }}>
                          SUMMARY
                        </div>
                        <div style={{
                          fontSize: '0.75rem',
                          lineHeight: 1.4,
                          color: '#334155',
                          background: '#e0f2fe',
                          padding: '0.5rem',
                          borderRadius: '4px',
                        }}>
                          {chunk.summary.length > 150 ? chunk.summary.slice(0, 150) + '...' : chunk.summary}
                        </div>
                      </div>
                    )}

                    <div>
                      <div style={{ fontSize: '0.65rem', color: '#94a3b8', marginBottom: '0.25rem', fontWeight: 600 }}>
                        CONTENT
                      </div>
                      <div style={{
                        fontSize: '0.75rem',
                        lineHeight: 1.4,
                        color: '#475569',
                        maxHeight: '100px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                        {chunk.raw_content.length > 200 ? chunk.raw_content.slice(0, 200) + '...' : chunk.raw_content}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default DocumentViewerModal;
